/**
 * i18n Configuration
 * Sets up internationalization with React i18next and Carbon Design System
 */

import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import HttpApi from 'i18next-http-backend';
import {
  DEFAULT_LANGUAGE,
  SUPPORTED_LANGUAGES,
  type SupportedLanguage,
} from '../../../shared/interfaces/i18n.interface';

// Import translation resources
import enTranslations from './locales/en';
import ptBRTranslations from './locales/pt-BR';
import ptPTTranslations from './locales/pt-PT';
import esTranslations from './locales/es';

// Cloudflare geo-location detection
const cloudflareGeoDetector = {
  name: 'cloudflareGeo',
  lookup() {
    // Check Cloudflare headers for country/region
    const cfCountry = (window as any).__CF_COUNTRY__;
    const cfContinent = (window as any).__CF_CONTINENT__;

    // Map countries to languages
    if (cfCountry === 'BR') {
      return 'pt-BR';
    }
    if (cfCountry === 'PT') {
      return 'pt-PT';
    }
    if (
      cfCountry === 'ES' ||
      cfCountry === 'MX' ||
      cfCountry === 'AR' ||
      cfCountry === 'CO' ||
      cfCountry === 'PE' ||
      cfCountry === 'CL'
    ) {
      return 'es';
    }

    // Default to English
    return 'en';
  },
  cacheUserLanguage(lng: string) {
    localStorage.setItem('i18nextLng', lng);
  },
};

// Detection options
const detection = {
  // Order of detection methods
  order: ['localStorage', 'cookie', 'querystring', 'cloudflareGeo', 'navigator', 'htmlTag'],

  // Cache options
  caches: ['localStorage', 'cookie'],

  // Cookie options
  cookieName: 'sigma08_language',
  cookieDomain: window.location.hostname,
  cookieOptions: {
    path: '/',
    sameSite: 'strict' as const,
    secure: true,
  },

  // Query string parameter
  lookupQuerystring: 'lng',

  // Local storage key
  lookupLocalStorage: 'sigma08_language',

  // Check HTML lang attribute
  htmlTag: document.documentElement,

  // Custom detector for Cloudflare
  customDetectors: [cloudflareGeoDetector],
};

// Initialize i18n
i18n
  .use(HttpApi)
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    // Resources
    resources: {
      en: enTranslations,
      'pt-BR': ptBRTranslations,
      'pt-PT': ptPTTranslations,
      es: esTranslations,
    },

    // Language settings
    lng: DEFAULT_LANGUAGE,
    fallbackLng: DEFAULT_LANGUAGE,
    supportedLngs: SUPPORTED_LANGUAGES,

    // Detection
    detection,

    // Namespaces
    defaultNS: 'common',
    ns: ['common', 'navigation', 'marketing', 'dashboard', 'auth', 'educational', 'errors', 'validation'],

    // Interpolation
    interpolation: {
      escapeValue: false, // React already escapes values
      formatSeparator: ',',
      format: (value, format, lng) => {
        // Custom formatting for dates, numbers, currency
        if (format === 'currency') {
          return formatCurrency(value, lng as SupportedLanguage);
        }
        if (format === 'date') {
          return formatDate(value, lng as SupportedLanguage);
        }
        if (format === 'number') {
          return formatNumber(value, lng as SupportedLanguage);
        }
        return value;
      },
    },

    // React specific
    react: {
      bindI18n: 'languageChanged',
      bindI18nStore: 'added removed',
      useSuspense: !window.location.hostname.includes('staging'), // Disable suspense in staging due to API issues
      transSupportBasicHtmlNodes: true,
      transKeepBasicHtmlNodesFor: ['br', 'strong', 'i', 'p'],
    },

    // Backend options for dynamic loading
    backend: {
      loadPath: '/api/i18n/{{lng}}/{{ns}}',
      addPath: '/api/i18n/add/{{lng}}/{{ns}}',
      allowMultiLoading: false,
      crossDomain: false,
      withCredentials: true,
      overrideMimeType: false,
      requestOptions: {
        cache: 'default',
      },
    },

    // Debug in development
    debug: import.meta.env.DEV,

    // Performance
    load: 'languageOnly',
    cleanCode: true,
    nonExplicitSupportedLngs: false,

    // Save missing keys
    saveMissing: import.meta.env.DEV,
    saveMissingTo: 'current',
    missingKeyHandler: (lngs, ns, key, fallbackValue) => {
      if (import.meta.env.DEV) {
        console.warn(`Missing translation: ${lngs.join(', ')} - ${ns}:${key}`);
      }
    },
  });

// Formatting helpers
function formatCurrency(value: number, language: SupportedLanguage): string {
  const localeMap = {
    en: 'en-US',
    'pt-BR': 'pt-BR',
    'pt-PT': 'pt-PT',
    es: 'es-ES',
  };
  const currencyMap = {
    en: 'USD',
    'pt-BR': 'BRL',
    'pt-PT': 'EUR',
    es: 'EUR',
  };

  const locale = localeMap[language] || 'en-US';
  const currency = currencyMap[language] || 'USD';

  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
  }).format(value);
}

function formatDate(value: Date | string, language: SupportedLanguage): string {
  const localeMap = {
    en: 'en-US',
    'pt-BR': 'pt-BR',
    'pt-PT': 'pt-PT',
    es: 'es-ES',
  };

  const locale = localeMap[language] || 'en-US';
  const date = typeof value === 'string' ? new Date(value) : value;

  return new Intl.DateTimeFormat(locale, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  }).format(date);
}

function formatNumber(value: number, language: SupportedLanguage): string {
  const localeMap = {
    en: 'en-US',
    'pt-BR': 'pt-BR',
    'pt-PT': 'pt-PT',
    es: 'es-ES',
  };

  const locale = localeMap[language] || 'en-US';
  return new Intl.NumberFormat(locale).format(value);
}

// Export configured instance
export default i18n;

// Export helper to get current language
export const getCurrentLanguage = (): SupportedLanguage => {
  return i18n.language as SupportedLanguage;
};

// Export helper to change language
export const changeLanguage = async (language: SupportedLanguage): Promise<void> => {
  await i18n.changeLanguage(language);

  // Update HTML lang attribute
  document.documentElement.lang = language;

  // Update Carbon locale if needed
  if (window.CarbonComponents) {
    const carbonLocaleMap = {
      en: 'en-US',
      'pt-BR': 'pt-BR',
      'pt-PT': 'pt-PT',
      es: 'es-ES',
    };
    window.CarbonComponents.settings.locale = carbonLocaleMap[language] || 'en-US';
  }

  // Store preference
  localStorage.setItem('sigma08_language', language);

  // Set cookie for server-side detection
  document.cookie = `sigma08_language=${language}; path=/; max-age=31536000; SameSite=Strict; Secure`;
};

// Export helper to detect user's preferred language
export const detectUserLanguage = (): SupportedLanguage => {
  // Check stored preference first
  const stored = localStorage.getItem('sigma08_language');
  if (stored && SUPPORTED_LANGUAGES.includes(stored as SupportedLanguage)) {
    return stored as SupportedLanguage;
  }

  // Check browser language
  const browserLang = navigator.language.toLowerCase();
  if (browserLang === 'pt-br' || browserLang.startsWith('pt-br')) {
    return 'pt-BR';
  }
  if (browserLang === 'pt-pt' || browserLang.startsWith('pt-pt')) {
    return 'pt-PT';
  }
  if (browserLang.startsWith('pt')) {
    return 'pt-BR'; // Default Portuguese to Brazilian
  }
  if (browserLang.startsWith('es')) {
    return 'es';
  }

  return DEFAULT_LANGUAGE;
};

// Type augmentation for Carbon compatibility
declare global {
  interface Window {
    CarbonComponents?: {
      settings: {
        locale: string;
      };
    };
    __CF_COUNTRY__?: string;
    __CF_CONTINENT__?: string;
  }
}
